import React, { useState, useEffect } from "react";
import { Bell, Mail, MessageSquare, Loader2, Save } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { usePortalAuth } from "./PortalLayout";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PortalSettings = () => {
  const { customer } = usePortalAuth();
  const [preferences, setPreferences] = useState({ job_updates: false, invoice_created: false, invoice_reminder: false, sms_enabled: false, email_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customer?.notification_preferences) setPreferences(customer.notification_preferences);
    setLoading(false);
  }, [customer]);

  const handleToggle = (key) => setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    const token = localStorage.getItem("portal_token");
    setSaving(true);
    try {
      await axios.put(`${API}/portal/profile/notifications`, preferences, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preferences saved!");
    } catch { toast.error("Failed to save preferences"); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0046BE]" /></div>;

  return (
    <div className="space-y-5 sm:space-y-6 max-w-2xl" data-testid="portal-settings">
      <h1 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Outfit']">Settings</h1>

      {/* Profile Info */}
      <div className="bg-white rounded-2xl overflow-hidden" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
        <div className="p-5 sm:p-6" style={{borderBottom: '1px solid rgba(0,0,0,0.04)'}}>
          <h2 className="text-sm font-semibold text-slate-700 font-['Outfit']">Profile Information</h2>
          <p className="text-xs text-slate-300 mt-0.5">Your account details</p>
        </div>
        <div className="p-5 sm:p-6">
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { label: "Name", value: customer?.name },
              { label: "Email", value: customer?.email },
              { label: "Phone", value: customer?.phone },
              { label: "Address", value: customer?.address ? `${customer.address}${customer.city ? `, ${customer.city}` : ''}${customer.state ? ` ${customer.state}` : ''}${customer.zip ? ` ${customer.zip}` : ''}` : null },
            ].map((field) => (
              <div key={field.label}>
                <p className="text-xs text-slate-300 mb-1">{field.label}</p>
                <p className="text-sm font-medium text-slate-700">{field.value || "N/A"}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-300 mt-5 pt-4" style={{borderTop: '1px solid rgba(0,0,0,0.04)'}}>
            To update your profile information, please contact your service provider.
          </p>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white rounded-2xl overflow-hidden" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
        <div className="p-5 sm:p-6" style={{borderBottom: '1px solid rgba(0,0,0,0.04)'}}>
          <h2 className="text-sm font-semibold text-slate-700 font-['Outfit'] flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-400" /> Notification Preferences
          </h2>
          <p className="text-xs text-slate-300 mt-0.5">Choose how you want to be notified</p>
        </div>
        <div className="p-5 sm:p-6 space-y-5">
          <div className="space-y-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Notification Types</p>
            {[
              { id: "job_updates", label: "Job Updates", desc: "Status changes on your jobs" },
              { id: "invoice_created", label: "New Invoices", desc: "When a new invoice is created" },
              { id: "invoice_reminder", label: "Invoice Reminders", desc: "Reminders for unpaid invoices" },
            ].map((item) => (
              <div key={item.id} className="flex items-center justify-between py-1">
                <div>
                  <Label htmlFor={item.id} className="text-sm font-medium text-slate-700 cursor-pointer">{item.label}</Label>
                  <p className="text-xs text-slate-300 mt-0.5">{item.desc}</p>
                </div>
                <Switch id={item.id} checked={preferences[item.id]} onCheckedChange={() => handleToggle(item.id)} data-testid={`toggle-${item.id}`} />
              </div>
            ))}
          </div>

          <div className="pt-5 space-y-4" style={{borderTop: '1px solid rgba(0,0,0,0.04)'}}>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Delivery Methods</p>
            {[
              { id: "email_enabled", icon: Mail, label: "Email", desc: "Receive via email" },
              { id: "sms_enabled", icon: MessageSquare, label: "SMS", desc: "Receive via text message" },
            ].map((item) => (
              <div key={item.id} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-3">
                  <item.icon className="w-4 h-4 text-slate-300" />
                  <div>
                    <Label htmlFor={item.id} className="text-sm font-medium text-slate-700 cursor-pointer">{item.label}</Label>
                    <p className="text-xs text-slate-300 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <Switch id={item.id} checked={preferences[item.id]} onCheckedChange={() => handleToggle(item.id)} data-testid={`toggle-${item.id}`} />
              </div>
            ))}
          </div>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#0046BE] hover:bg-[#0033A0] text-white rounded-xl h-9 px-5" data-testid="save-preferences-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save Preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalSettings;
